import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useKanbanStore } from '@/stores/kanban'
import MainLayout from '@/layouts/MainLayout.vue'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      name: 'home',
      redirect: '/kanban'
    },
    {
      path: '/login',
      name: 'login',
      component: () => import('@/views/LoginView.vue'),
      meta: { requiresAuth: false }
    },
    // V8 Module 2 — Public job board (no auth required)
    {
      path: '/jobs/:orgSlug',
      name: 'public-jobs',
      component: () => import('@/views/PublicJobsView.vue'),
      meta: { requiresAuth: false }
    },
    {
      path: '/jobs/:orgSlug/:jobSlug',
      name: 'public-job-detail',
      component: () => import('@/views/PublicJobDetailView.vue'),
      meta: { requiresAuth: false }
    },
    {
      path: '/',
      component: MainLayout,
      meta: { requiresAuth: true },
      children: [
        {
          path: '/kanban',
          name: 'kanban',
          component: () => import('@/views/KanbanView.vue')
        },
        {
          path: '/inbox',
          name: 'inbox',
          component: () => import('@/views/InboxView.vue')
        },
        {
          path: '/screening',
          name: 'screening',
          component: () => import('@/views/ScreeningView.vue')
        },
        {
          path: '/workflows',
          name: 'workflows',
          component: () => import('@/views/WorkflowsView.vue'),
          meta: { requiresPermission: 'manage_workflows' }
        },
        {
          path: '/candidates',
          name: 'candidates',
          component: () => import('@/views/CandidatesView.vue'),
          meta: { requiresPermission: 'view_candidates' }
        },
        {
          path: '/positions',
          name: 'positions',
          component: () => import('@/views/PositionsView.vue'),
          meta: { requiresPermission: 'view_positions' }
        },
        {
          path: '/interviews',
          name: 'interviews',
          component: () => import('@/views/InterviewsView.vue'),
          meta: { requiresPermission: 'view_interviews' }
        },
        {
          path: '/history',
          name: 'history',
          component: () => import('@/views/HistoryView.vue'),
          meta: { requiresPermission: 'view_analyses' }
        },
        {
          path: '/analyze',
          name: 'analyze',
          component: () => import('@/views/AnalysisView.vue'),
          meta: { requiresPermission: 'create_single_analysis' }
        },
        {
          path: '/admin',
          name: 'admin',
          component: () => import('@/views/AdminView.vue'),
          meta: { requiresPermission: 'manage_organization' }
        },
        {
          path: '/master-admin',
          name: 'master-admin',
          component: () => import('@/views/MasterAdminView.vue'),
          meta: { requiresMasterAdmin: true }
        },
        {
          path: '/settings',
          name: 'settings',
          component: () => import('@/views/SettingsView.vue')
        }
      ]
    }
  ]
})

// Navigation guards
router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore()

  // Check authentication
  if (to.meta.requiresAuth && !authStore.isAuthenticated) {
    next('/login')
    return
  }

  // Handle deep linking from email notifications
  if (to.name === 'inbox' && to.query.applicationId && authStore.isAuthenticated) {
    // Store the application ID to open the modal after the view loads
    const kanbanStore = useKanbanStore()
    setTimeout(() => {
      kanbanStore.setSelectedApplication(to.query.applicationId as string)
    }, 500)
  }

  // Check master admin access
  if (to.meta.requiresMasterAdmin && !authStore.isMasterAdmin) {
    next('/dashboard')
    return
  }

  // Check permissions
  if (to.meta.requiresPermission) {
    const permission = to.meta.requiresPermission as string
    if (!authStore.hasPermission(permission)) {
      next('/dashboard')
      return
    }
  }

  next()
})

export default router
